import { Link } from 'react-router-dom';
import { Play, Heart, Clock, Lock } from 'lucide-react';
import { useState } from 'react';
import { toggleFavorite } from '@/lib/api';
import { toast } from 'sonner';

export const AudioCard = ({ track, isFavorite = false, onFavoriteChange, comingSoon = false }) => {
  const [favorite, setFavorite] = useState(isFavorite);
  const [isHovered, setIsHovered] = useState(false);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    if (mins >= 60) {
      const hours = Math.floor(mins / 60);
      const remainMins = mins % 60;
      return `${hours}h ${remainMins}m`;
    }
    return `${mins} min`;
  };

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const response = await toggleFavorite(track.id);
      setFavorite(response.data.is_favorite);
      onFavoriteChange?.(track.id, response.data.is_favorite);
      toast.success(response.data.is_favorite ? 'Added to favorites' : 'Removed from favorites');
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const Wrapper = comingSoon ? 'div' : Link;
  const wrapperProps = comingSoon ? {} : { to: `/player/${track.id}` };

  return (
    <Wrapper
      {...wrapperProps}
      className={`audio-card group block ${comingSoon ? 'cursor-default' : ''}`}
      data-testid={`audio-card-${track.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative bg-zinc-900/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/5 hover:border-white/10 transition-colors duration-300 card-hover">
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={track.image_url}
            alt={track.title}
            className={`audio-card-img w-full h-full object-cover ${comingSoon ? 'grayscale-[40%]' : ''}`}
            loading="lazy"
          />
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/40 to-transparent" />
          
          {/* Coming Soon badge */}
          {comingSoon && (
            <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 bg-zinc-900/80 backdrop-blur-sm border border-white/10 rounded-full px-3 py-1.5">
              <Lock className="w-3 h-3 text-amber-400" />
              <span className="text-xs font-medium text-amber-400">Coming Soon</span>
            </div>
          )}

          {/* Play button - only show if not coming soon */}
          {!comingSoon && (
            <div 
              className={`
                absolute inset-0 flex items-center justify-center
                transition-opacity duration-300
                ${isHovered ? 'opacity-100' : 'opacity-0'}
              `}
            >
              <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform">
                <Play className="w-6 h-6 text-zinc-900 ml-1" fill="currentColor" />
              </div>
            </div>
          )}

          {/* Favorite button - only show if not coming soon */}
          {!comingSoon && (
            <button
              onClick={handleFavorite}
              className={`
                absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center
                transition-all duration-300
                ${favorite 
                  ? 'bg-violet-600 text-white' 
                  : 'bg-black/40 backdrop-blur-sm text-white/70 hover:text-white hover:bg-black/60'
                }
              `}
              data-testid={`favorite-btn-${track.id}`}
            >
              <Heart className="w-4 h-4" fill={favorite ? 'currentColor' : 'none'} />
            </button>
          )}

          {/* Duration badge */}
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
            <Clock className="w-3.5 h-3.5 text-zinc-400" />
            <span className="text-xs text-zinc-300">{formatDuration(track.duration)}</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-medium text-zinc-100 line-clamp-1 group-hover:text-white transition-colors">
            {track.title}
          </h3>
          <p className="text-sm text-zinc-500 line-clamp-2 mt-1">
            {track.description}
          </p>
        </div>
      </div>
    </Wrapper>
  );
};

export default AudioCard;
